import { BoxProps as MuiBoxProps } from '@mui/material/Box';

import { Box } from './Box';
import { useTheme } from './muiTheme/utils';

export type MaterialVariant =
  | 'ultraThin'
  | 'thin'
  | 'regular'
  | 'thick'
  | 'ultraThick'
  | 'accessory'
  | 'glassSmall'
  | 'glassMedium'
  | 'glassLarge'
  | 'glassClear'
  | 'glassPrimary'
  | 'glassError';

export interface MaterialProps extends MuiBoxProps {
  variant?: MaterialVariant;
  borderRadius?: number;
  blur?: number;
}

const borderRadiusValues = {
  paper: 4.75,
  paperOutlined: 4.75,
  ultraThin: 4.75,
  thin: 4.75,
  regular: 4.75,
  thick: 4.75,
  ultraThick: 4.75,
  accessory: 4.75,
  glassSmall: '50vh',
  glassError: '50vh',
  glassPrimary: '50vh',
  glassMedium: 4.75,
  glassLarge: 4.75,
  glassClear: 4.75,
} as Record<MaterialVariant, string | number>;

export function Material<C extends React.ElementType = 'div'>(
  props: MuiBoxProps<C, { component?: C }> & MaterialProps
) {
  const {
    sx,
    children,
    borderRadius,
    blur,
    variant = 'paper',
    ...otherProps
  } = props;

  const theme = useTheme();

  const isMaterial =
    variant === 'ultraThin' ||
    variant === 'thin' ||
    variant === 'regular' ||
    variant === 'thick' ||
    variant === 'ultraThick' ||
    variant === 'accessory';
  const isGlass =
    variant === 'glassSmall' ||
    variant === 'glassMedium' ||
    variant === 'glassLarge' ||
    variant === 'glassPrimary' ||
    variant === 'glassError' ||
    variant === 'glassClear';

  const isTinted = variant === 'glassPrimary' || variant === 'glassError';

  const isFullyRounded =
    isTinted || variant === 'glassSmall' || variant === 'glassClear';

  const commonOverlayStyle = {
    borderRadius: borderRadius || borderRadiusValues[variant],
    cornerShape: isFullyRounded ? 'rounded' : 'superellipse(1)',
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
  };

  const baseLayerStyle = [
    {
      p: 3.25,
      cornerShape: isFullyRounded ? 'rounded' : 'superellipse(1)',
      borderRadius: borderRadius || borderRadiusValues[variant],
      position: 'relative',

      '& .material-blur': {
        ...commonOverlayStyle,
        backdropFilter: blur ? `blur(${blur})` : 'blur(calc(30px / 2))',
        willChange: 'backdrop-filter',
      },

      '&.material-material': {},

      '&.material-glass': {
        boxShadow: '0px 5px 20px rgba(0, 0, 0, 0.12)',
        '& .material-overlay1': {},
        '& .material-overlay2': {},
        '& .material-border1': {
          border: '1px solid transparent',
          backgroundRepeat: 'no-repeat',
          mixBlendMode: 'overlay',
          mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
        },
        '& .material-border2': {
          border: '1px solid transparent',
          backgroundRepeat: 'no-repeat',
          mixBlendMode: 'overlay',
          mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
        },
      },

      '& .material-overlay1': {
        ...commonOverlayStyle,
      },
      '& .material-overlay2': {
        ...commonOverlayStyle,
      },
      '& .material-overlay3': {
        ...commonOverlayStyle,
      },
      '& .material-border1': {
        ...commonOverlayStyle,
      },
      '& .material-border2': {
        ...commonOverlayStyle,
      },
      '& .material-tint1': {
        ...commonOverlayStyle,
      },
      '& .material-tint2': {
        ...commonOverlayStyle,
      },

      '&.material-ultraThin': {
        '& .material-overlay1': {
          backgroundColor: 'rgba(246, 246, 246, 0.36)',
          ...theme.applyStyles('dark', {
            backgroundColor: 'rgba(18, 18, 18, 0.40)',
          }),
        },
      },
      '&.material-thin': {
        '& .material-overlay1': {
          backgroundColor: 'rgba(246, 246, 246, 0.48)',
          ...theme.applyStyles('dark', {
            backgroundColor: 'rgba(18, 18, 18, 0.50)',
          }),
        },
      },
      '&.material-regular': {
        '& .material-overlay1': {
          backgroundColor: 'rgba(246, 246, 246, 0.60)',
          ...theme.applyStyles('dark', {
            backgroundColor: 'rgba(18, 18, 18, 0.60)',
          }),
        },
      },
      '&.material-thick': {
        '& .material-overlay1': {
          backgroundColor: 'rgba(246, 246, 246, 0.76)',
          ...theme.applyStyles('dark', {
            backgroundColor: 'rgba(18, 18, 18, 0.70)',
          }),
        },
      },
      '&.material-ultraThick': {
        '& .material-overlay1': {
          backgroundColor: 'rgba(246, 246, 246, 0.84)',
          ...theme.applyStyles('dark', {
            backgroundColor: 'rgba(18, 18, 18, 0.80)',
          }),
        },
      },
      '&.material-accessory': {
        '& .material-overlay1': {
          backgroundColor: 'rgba(225, 225, 225, 0.50)',
          ...theme.applyStyles('dark', {
            backgroundColor: 'rgba(51, 51, 51, 0.50)',
          }),
        },
      },

      '&.material-glassSmall': {
        '& .material-overlay1': {
          mixBlendMode: 'lighten',
          backgroundColor: 'rgba(255, 255, 255, 0.40)',
          ...theme.applyStyles('dark', {
            mixBlendMode: 'darken',
            backgroundColor: 'rgba(0, 0, 0, 0.40)',
          }),
        },
        '& .material-overlay2': {
          mixBlendMode: 'darken',
          backgroundColor: 'rgba(250, 250, 250, 1.00)',
          ...theme.applyStyles('dark', {
            mixBlendMode: 'lighten',
            backgroundColor: 'rgba(26, 26, 26, 1.00)',
          }),
        },
        '& .material-overlay3': {},
        '& .material-tint1': {},
        '& .material-tint2': {},
        '& .material-border1': {
          background:
            'linear-gradient(145deg, rgba(255, 255, 255, 0.90) 16.47%, rgba(255, 255, 255, 0.35) 23.29%, rgba(255, 255, 255, 0.15) 50.59%, rgba(255, 255, 255, 0.35) 77.88%, rgba(255, 255, 255, 0.90) 84.71%) border-box',
        },
        '& .material-border2': {},
      },

      '&.material-glassMedium': {
        '& .material-overlay1': {
          mixBlendMode: 'lighten',
          backgroundColor: 'rgba(255, 255, 255, 0.70)',
          ...theme.applyStyles('dark', {
            mixBlendMode: 'darken',
            backgroundColor: 'rgba(0, 0, 0, 0.40)',
          }),
        },
        '& .material-overlay2': {
          mixBlendMode: 'darken',
          backgroundColor: 'rgba(250, 250, 250, 1.00)',
          ...theme.applyStyles('dark', {
            mixBlendMode: 'lighten',
            backgroundColor: 'rgba(26, 26, 26, 1.00)',
          }),
        },
        '& .material-overlay3': {},
        '& .material-tint1': {},
        '& .material-tint2': {},
        '& .material-border1': {
          background:
            'linear-gradient(145deg, rgba(255, 255, 255, 0.90) 16.47%, rgba(255, 255, 255, 0.85) 23.29%, rgba(255, 255, 255, 0.80) 50.59%, rgba(255, 255, 255, 0.85) 77.88%, rgba(255, 255, 255, 0.90) 84.71%) border-box',
        },
        '& .material-border2': {},
      },

      '&.material-glassLarge': {
        '& .material-overlay1': {
          mixBlendMode: 'lighten',
          backgroundColor: 'rgba(255, 255, 255, 0.70)',
          ...theme.applyStyles('dark', {
            mixBlendMode: 'darken',
            backgroundColor: 'rgba(0, 0, 0, 0.40)',
          }),
        },
        '& .material-overlay2': {
          mixBlendMode: 'darken',
          backgroundColor: 'rgba(250, 250, 250, 1.00)',
          ...theme.applyStyles('dark', {
            mixBlendMode: 'lighten',
            backgroundColor: 'rgba(26, 26, 26, 1.00)',
          }),
        },
        '& .material-overlay3': {},
        '& .material-tint1': {},
        '& .material-tint2': {},
        '& .material-border1': {
          background:
            'linear-gradient(145deg, rgba(255, 255, 255, 0.90) 16.47%, rgba(255, 255, 255, 0.85) 23.29%, rgba(255, 255, 255, 0.80) 50.59%, rgba(255, 255, 255, 0.85) 77.88%, rgba(255, 255, 255, 0.90) 84.71%) border-box',
        },
        '& .material-border2': {},
      },

      '&.material-glassClear': {
        '& .material-overlay1': {
          mixBlendMode: 'color-dodge',
          backgroundColor: 'rgba(255, 255, 255, 0.07)',
          ...theme.applyStyles('dark', {
            mixBlendMode: 'color-dodge',
            backgroundColor: 'rgba(255, 255, 255, 0.07)',
          }),
        },
        '& .material-overlay2': {},
        '& .material-overlay3': {},
        '& .material-tint1': {},
        '& .material-tint2': {},
        '& .material-border1': {
          background:
            'linear-gradient(145deg, rgba(255, 255, 255, 0.90) 16.47%, rgba(255, 255, 255, 0.35) 23.29%, rgba(255, 255, 255, 0.15) 50.59%, rgba(255, 255, 255, 0.35) 77.88%, rgba(255, 255, 255, 0.90) 84.71%) border-box',
        },
        '& .material-border2': {},
      },

      '&.material-glassPrimary': {
        '& .material-overlay1': {
          mixBlendMode: 'lighten',
          backgroundColor: 'rgba(255, 255, 255, 0.40)',
          ...theme.applyStyles('dark', {
            mixBlendMode: 'lighten',
            backgroundColor: 'rgba(255, 255, 255, 0.40)',
          }),
        },
        '& .material-overlay2': {
          mixBlendMode: 'darken',
          backgroundColor: 'rgba(250, 250, 250, 1.00)',
          ...theme.applyStyles('dark', {
            mixBlendMode: 'darken',
            backgroundColor: 'rgba(250, 250, 250, 1.00)',
          }),
        },
        '& .material-overlay3': {},
        '& .material-tint1': {
          bgcolor: 'primary.solid',
          mixBlendMode: 'overlay',
          opacity: 0.5,
        },
        '& .material-tint2': {
          bgcolor: 'primary.solid',
          mixBlendMode: 'multiply',
          opacity: 1.0,
        },
        '& .material-border1': {
          background:
            'linear-gradient(145deg, rgba(255, 255, 255, 0.90) 16.47%, rgba(255, 255, 255, 0.35) 23.29%, rgba(255, 255, 255, 0.15) 50.59%, rgba(255, 255, 255, 0.35) 77.88%, rgba(255, 255, 255, 0.90) 84.71%) border-box',
        },
        '& .material-border2': {},
      },

      '&.material-glassError': {
        '& .material-overlay1': {
          mixBlendMode: 'lighten',
          backgroundColor: 'rgba(255, 255, 255, 0.40)',
          ...theme.applyStyles('dark', {
            mixBlendMode: 'lighten',
            backgroundColor: 'rgba(255, 255, 255, 0.40)',
          }),
        },
        '& .material-overlay2': {
          mixBlendMode: 'darken',
          backgroundColor: 'rgba(250, 250, 250, 1.00)',
          ...theme.applyStyles('dark', {
            mixBlendMode: 'darken',
            backgroundColor: 'rgba(250, 250, 250, 1.00)',
          }),
        },
        '& .material-overlay3': {},
        '& .material-tint1': {
          bgcolor: 'error.solid',
          mixBlendMode: 'overlay',
          opacity: 0.5,
        },
        '& .material-tint2': {
          bgcolor: 'error.solid',
          mixBlendMode: 'multiply',
          opacity: 1.0,
        },
        '& .material-border1': {
          background:
            'linear-gradient(145deg, rgba(255, 255, 255, 0.90) 16.47%, rgba(255, 255, 255, 0.35) 23.29%, rgba(255, 255, 255, 0.15) 50.59%, rgba(255, 255, 255, 0.35) 77.88%, rgba(255, 255, 255, 0.90) 84.71%) border-box',
        },
        '& .material-border2': {},
      },
    },
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  const getBaseClassName = () => {
    return `material-${variant} ${isMaterial ? 'material-material' : ''} ${isGlass ? 'material-glass' : ''}  ${isTinted ? 'material-tint' : ''}`;
  };

  return (
    <Box className={getBaseClassName()} sx={baseLayerStyle} {...otherProps}>
      <Box className={`material-blur`}></Box>

      <Box className={`material-overlay1`}></Box>
      <Box className={`material-overlay2`}></Box>
      <Box className={`material-overlay3`}></Box>

      <Box className={`material-tint1`}></Box>
      <Box className={`material-tint2`}></Box>

      <Box className={`material-border1`}></Box>
      <Box className={`material-border2`}></Box>
      <div style={{ position: 'relative' }}>{children}</div>
    </Box>
  );
}
